import { Fragment, useEffect, useRef, useState, type RefObject } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { Badge, cn } from '@hrms/ui';
import { useAuthenticatedSession } from '../session/SessionProvider.js';
import { ADMIN_NAV, NAV, visibleNav, type NavItem } from './nav.js';
import { useNavSwitches } from './useNavSwitches.js';
import { ICONS } from './navIcons.js';
import { BrandMark } from '../components/BrandMark.js';
import { SetupNavItem } from '../features/setup/SetupNavItem.js';
import { PRODUCT_NAME } from '../lib/brand.js';

export interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  /** Focus returns here when the drawer closes — the topbar's own menu trigger button. */
  returnFocusRef: RefObject<HTMLElement | null>;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';

/**
 * Below `lg` there is no sidebar at all (see AppShell.tsx's `Sidebar()`), so this is the only way
 * to navigate the app on a phone or narrow tablet. A left-edge drawer, not a bottom bar: `NAV` and
 * `ADMIN_NAV` together routinely exceed a bottom bar's ~5 usable slots.
 *
 * Lives in `apps/web`, not `packages/ui`, because it's welded to `react-router-dom` and the
 * session actor (`@hrms/ui` is deliberately router-free). Reuses the exact `--sidebar-*` tokens
 * the desktop rail uses so it reads as the same navigation surface, just touch-triggered.
 *
 * Always mounted by AppShell (not conditionally rendered by the caller) so this component's own
 * effects can run their cleanup on the `open → false` transition (returning focus, unlocking
 * scroll) — it renders `null` internally instead. No exit animation: per the "functional only"
 * motion decision, an unmount is the close, not an `animate-out` class.
 */
export function MobileNav({ open, onClose, returnFocusRef }: MobileNavProps) {
  const { actor, me } = useAuthenticatedSession();
  const switches = useNavSwitches();
  const primary = visibleNav(actor, NAV, me.enabledModules, switches);
  const admin = visibleNav(actor, ADMIN_NAV, me.enabledModules, switches);
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Escape closes; Tab is trapped inside the panel — `aria-modal` alone doesn't stop keyboard
  // focus from reaching the visually-obscured page behind the scrim, only assistive tech honors it.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Focus the close button on open (not the first link — a person opening the drawer is oriented
  // by the header, not dropped straight into the nav list); return focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    return () => {
      returnFocusRef.current?.focus();
    };
  }, [open, returnFocusRef]);

  // Body scroll lock — capture whatever was there before (never assume it was unset) and restore
  // that exact value, not an empty string, so this can't clobber some other feature's own lock.
  useEffect(() => {
    if (!open) return;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = priorOverflow;
    };
  }, [open]);

  // Crossing back above `lg` (the same breakpoint Sidebar()'s `lg:flex` uses) closes the drawer —
  // the desktop rail is about to reappear, so a still-open drawer would double up the nav.
  useEffect(() => {
    if (!open) return;
    const query = window.matchMedia('(min-width: 64rem)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) onClose();
    };
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, [open, onClose]);

  // Belt-and-braces alongside every link's own onClick: closes on any pathname change, including
  // one triggered some other way than a click inside this drawer (e.g. browser back/forward), and
  // covers clicking the already-active route, which fires no `onClick`-observable change at all.
  // Deliberately keyed on `location.pathname` alone: `open`/`onClose` should never make this
  // effect re-fire on their own, only ride along with whatever they are at the next pathname change.
  useEffect(() => {
    if (open) onClose();
  }, [location.pathname]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        id="mobile-nav"
        className="flex h-dvh w-[min(17.5rem,85vw)] flex-col gap-4 border-r border-sidebar-border bg-sidebar-bg px-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1.25rem,env(safe-area-inset-top))] animate-in slide-in-from-left"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between px-2">
          <span className="flex items-center gap-2.5">
            <BrandMark size={28} className="ring-1 ring-sidebar-fg/25" />
            <span className="font-display text-base font-bold text-sidebar-fg">{PRODUCT_NAME}</span>
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close navigation"
            onClick={onClose}
            className="flex size-10 items-center justify-center rounded-full text-sidebar-fg-muted transition-colors hover:bg-sidebar-hover-bg hover:text-sidebar-fg"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Unlike the desktop Sidebar(), Primary + Admin safely share ONE scrolling container
         * here: the desktop rail keeps Primary unscrolled specifically because `overflow-y: auto`
         * on an ancestor would clip Performance's `absolute left-full` hover flyout (see the
         * comment on Sidebar()). There is no flyout in this drawer — Performance's children render
         * inline via MobileNavItem's accordion instead — so that constraint doesn't apply, and
         * nothing here should be "fixed" back to two containers by analogy with the desktop rail. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SetupNavItem variant="drawer" onNavigate={onClose} />
          <nav className="flex flex-col gap-0.5" aria-label="Main">
            {primary.map((item) =>
              (item.children && item.children.length > 0) ||
              (item.subItems && item.subItems.length > 0) ? (
                <MobileNavItem key={item.to} item={item} onNavigate={onClose} />
              ) : (
                <MobileNavLink key={item.to} item={item} onNavigate={onClose} />
              ),
            )}
          </nav>

          {admin.length > 0 && (
            <nav className="flex flex-col gap-0.5" aria-label="Admin">
              <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-sidebar-fg-muted">
                Admin
              </p>
              {admin.map((item) =>
                (item.children && item.children.length > 0) ||
                (item.subItems && item.subItems.length > 0) ? (
                  <MobileNavItem key={item.to} item={item} onNavigate={onClose} />
                ) : (
                  <MobileNavLink key={item.to} item={item} onNavigate={onClose} />
                ),
              )}
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

/** A flat nav item — same treatment as the desktop rail's `SidebarLink`, plus closing the drawer. */
function MobileNavLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex min-h-11 items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
            : 'text-sidebar-fg-muted hover:bg-sidebar-hover-bg hover:text-sidebar-fg',
        )
      }
    >
      {ICONS[item.to]}
      {item.label}
    </NavLink>
  );
}

/**
 * The touch equivalent of the desktop rail's hover/focus flyout (`PerformanceNavItem`) — CSS
 * `group-hover:` never fires on touch, so a tap on the row expands an accordion of its sub-pages
 * inline (the usual mobile-app pattern) and a tap on a sub-page navigates and closes the drawer.
 * Defaults expanded when already inside this item's own section.
 */
function MobileNavItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const location = useLocation();
  const inSection = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  const [expanded, setExpanded] = useState(inSection);
  const childrenId = `mobile-nav-children-${item.to.replace(/\//g, '-')}`;

  const childLinks: { to: string; label: string; status?: 'live' | 'soon'; section?: string }[] =
    item.children
      ? item.children.map((child) => ({
          to: child.to,
          label: child.label,
          status: child.status,
          section: child.section,
        }))
      : (item.subItems ?? []).map((sub) => ({
          to: `${item.to}/${sub.path}`,
          label: sub.label,
          section: sub.section,
        }));

  return (
    <div className="flex flex-col gap-0.5">
      {/* The whole row toggles the submenu and does NOT navigate: `item.to` is a section root that
       * redirects to its first sub-page, so tapping the label used to land people on that first
       * submenu every time no matter which one they wanted. Sub-pages are reached from the list. */}
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={childrenId}
        onClick={() => setExpanded((value) => !value)}
        className={cn(
          'flex min-h-11 w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors',
          inSection
            ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
            : 'text-sidebar-fg-muted hover:bg-sidebar-hover-bg hover:text-sidebar-fg',
        )}
      >
        {ICONS[item.to]}
        <span className="flex-1">{item.label}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 transition-transform', expanded && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {expanded && (
        <div
          id={childrenId}
          className="flex flex-col gap-0.5 rounded-lg bg-sidebar-submenu-bg py-1.5 pl-9 pr-1.5"
        >
          {childLinks.map((child, index) => (
            <Fragment key={child.to}>
              {/* Same group headings as the desktop flyout (Payroll's Setup / Run / ...). */}
              {child.section && child.section !== childLinks[index - 1]?.section && (
                <p className="px-2.5 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-sidebar-fg-muted">
                  {child.section}
                </p>
              )}
              {child.status === 'soon' ? (
                <span
                  key={child.to}
                  className="flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm text-sidebar-fg-muted"
                >
                  {child.label}
                  <Badge tone="neutral">Coming soon</Badge>
                </span>
              ) : (
                <NavLink
                  key={child.to}
                  to={child.to}
                  end={child.to === item.to}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg'
                        : 'text-sidebar-fg-muted hover:bg-sidebar-hover-bg hover:text-sidebar-fg',
                    )
                  }
                >
                  {child.label}
                </NavLink>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
